import Analytics from 'analytics'

import googleAnalytics from '@analytics/google-analytics'
import { StatsigClient } from '@statsig/js-client'

import clarityPlugin from './clarity-plugin'

interface BaseProps {
  // Not implemented yet
  // client_type: string
  // client_name: string
  /** client ID. client_id is a reserved keyword for GA, so we use the prefix */
  veho_client_id?: string
}

interface DownloadAppProps {
  element: 'button' | 'banner_ad_1'
  platform: 'ios' | 'android'
}
interface TrackPackageProps extends BaseProps {
  tracking_code: string
  features?: 'cwa_map_view'
  is_authenticated?: boolean
  page_type?: 'home' | 'tracking_page' | 'business_hours'
}

interface TrackPackageErrorProps {
  tracking_code: string
  error_reason: 'unknown' | 'validation'
  features?: 'cwa_map_view'
  page_type?: 'home' | 'tracking_page' | 'business_hours'
}
interface ContactSupportProps {
  method: 'sms'
  features?: 'cwa_map_view'
}
interface ProvideFeedbackProps {
  method: 'google_form'
}
interface NavigateToMainSite {
  element: 'logo' | 'footer_link'
}
interface ReturnToTrackingPage {
  features?: 'cwa_map_view'
}

interface TokenLoginProps {
  features?: 'cwa_token_auth'
}
// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface ViewFaq {}

interface AddInstructions extends BaseProps {
  features?: 'cwa_map_view'
  // temp disabled, will be addressed in follow up
  // property_type: string
  // package_placement_preference: string
  // beware_of_dog: boolean
  // gated_community: boolean
  // security_code: boolean
}

interface AddInstructionsSuccess extends BaseProps {
  features?: 'cwa_map_view'
  // temp disabled, will be addressed in follow up
  // property_type: string
  // package_placement_preference: string
  // beware_of_dog: boolean
  // gated_community: boolean
  // security_code: boolean
}

interface AddInstructionsError extends BaseProps {
  error_reason: 'mismatched_phone' | 'unknown'
  features?: 'cwa_map_view'
  // temp disabled, will be addressed in follow up
  // property_type: string
  // package_placement_preference: string
  // beware_of_dog: boolean
  // gated_community: boolean
  // security_code: boolean
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface EditAddress extends BaseProps {
  // temp disabled, will be addressed in follow up
  // property_type: string
  // package_placement_preference: string
  // beware_of_dog: boolean
  // gated_community: boolean
  // security_code: boolean
}

interface EditAddressSuccess extends BaseProps {
  features?: 'cwa_map_view'
  // temp disabled, will be addressed in follow up
  // property_type: string
  // package_placement_preference: string
  // beware_of_dog: boolean
  // gated_community: boolean
  // security_code: boolean
}

interface EditAddressError extends BaseProps {
  error_reason: 'mismatched_phone' | 'unknown'
  features?: 'cwa_map_view'
  // temp disabled, will be addressed in follow up
  // property_type: string
  // package_placement_preference: string
  // beware_of_dog: boolean
  // gated_community: boolean
  // security_code: boolean
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface PAU_OriginalAddressSelection extends BaseProps {}

interface PAU_OriginalAddressSelectionSuccess extends BaseProps {
  features?: 'permanent_address_update'
}

interface PAU_OriginalAddressSelectionError extends BaseProps {
  error_reason: 'mismatched_phone' | 'unknown'
  features?: 'permanent_address_update'
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface PAU_VerifiedAddressSelection extends BaseProps {}

interface PAU_VerifiedAddressSelectionSuccess extends BaseProps {
  features?: 'permanent_address_update'
}

interface PAU_VerifiedAddressSelectionError extends BaseProps {
  error_reason: 'mismatched_phone' | 'unknown'
  features?: 'permanent_address_update'
}

interface GetAuthenticationCode extends BaseProps {
  auth_flow: 'instructions' | 'edit_address' | 'general' | 'permanent_address_update'
  features?: 'cwa_map_view'
}

interface GetAuthenticationCodeSuccess extends BaseProps {
  auth_flow: 'instructions' | 'edit_address' | 'general' | 'permanent_address_update'
  features?: 'cwa_map_view'
}

interface GetAuthenticationCodeError extends BaseProps {
  auth_flow: 'instructions' | 'edit_address' | 'general' | 'permanent_address_update'
  error_reason: 'verification_failed' | 'unknown'
  features?: 'cwa_map_view'
}
interface SubmitVerificationCode extends BaseProps {
  auth_flow: 'instructions' | 'edit_address' | 'general' | 'permanent_address_update'
  features?: 'cwa_map_view'
}
interface SubmitVerificationCodeSuccess extends BaseProps {
  auth_flow: 'instructions' | 'edit_address' | 'general' | 'permanent_address_update'
  features?: 'cwa_map_view'
}
interface SubmitVerificationCodeError extends BaseProps {
  auth_flow: 'instructions' | 'edit_address' | 'general' | 'permanent_address_update'
  error_reason: 'code_mismatch' | 'code_expired' | 'too_many_attempts' | 'unknown'
  features?: 'cwa_map_view'
}
interface ViewAuthentication extends BaseProps {
  verification_type: 'phone'
}
interface ViewInstructions extends BaseProps {
  trigger: 'manual' | 'auto'
}
interface ViewVerification extends BaseProps {
  verification_type: 'phone'
}

interface NavigateToPrivacyPage extends BaseProps {
  privacy_type: 'instructions' | 'footer_link'
}
interface NavigateToTermsPage extends BaseProps {
  terms_type: 'instructions'
}

interface NavigateToSecurityOverview extends BaseProps {
  element: 'footer_link'
}

interface NavigateToVulnerabilityReport extends BaseProps {
  element: 'footer_link'
}

interface FeedbackFormViewed {
  trackingId?: string
}

interface FeedbackFormSubmitted {
  trackingId?: string
  hadSupportInteraction: boolean
  overallDeliveryExperience: number | null
  negativeExperienceReasons: string[]
  driverSatisfaction: boolean | null
  trackingExperience: boolean | null
  trackingIssueReasons: string[]
  supportExperience: number | null
  supportIssueReasons: string[]
}

interface SupportFeedbackFormViewed {
  conversationId?: string
}

interface SupportFeedbackFormSubmitted {
  conversationId?: string
  overallSupportExperience: number | null
  supportIssues: string[]
  consentToFollowUp: boolean
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface DismissInstructions extends BaseProps {}

interface OpenPrivacyChoices extends BaseProps {
  element: 'footer_link'
}

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface TokenLoginSuccess extends TokenLoginProps {}

interface TokenLoginError extends TokenLoginProps {
  error_reason: 'internal_error' | 'firebase_error'
}

interface SaveBusinessHours extends BaseProps {
  is_authenticated: boolean
}

/**
 * Fake-door experiment (driver info): fired when the "driver info coming soon"
 * popup is shown after arriving via the `driverInfo=1` link in the
 * one-stop-away SMS. Counts click-throughs from the SMS link.
 */
interface DriverInfoPopupViewed extends BaseProps {
  tracking_code: string
  page_type: 'tracking_page'
}

/**
 * Fake-door experiment (driver info): fired when the consumer presses the
 * popup's CTA. Unlike `driver_info_popup_viewed` — which only means they tapped
 * the tracking link, something they may do for their ETA regardless — this is a
 * deliberate answer to "want to see your driver's info?", so it is the
 * experiment's demand signal.
 */
interface DriverInfoInterestConfirmed extends BaseProps {
  tracking_code: string
  page_type: 'tracking_page'
}

/**
 * Fake-door experiment (driver info): fired when the consumer explicitly
 * declines. Paired with `driver_info_interest_confirmed` this makes the answer
 * a two-sided question rather than a yes-or-silence one, and separates an
 * active "no" from simply closing the popup.
 */
interface DriverInfoInterestDeclined extends BaseProps {
  tracking_code: string
  page_type: 'tracking_page'
}

export interface EventMap {
  download_app: DownloadAppProps
  track_package: TrackPackageProps
  track_package_error: TrackPackageErrorProps
  contact_support: ContactSupportProps
  provide_feedback: ProvideFeedbackProps
  navigate_to_main_site: NavigateToMainSite
  return_to_tracking_page: ReturnToTrackingPage
  view_faq: ViewFaq
  add_instructions: AddInstructions
  add_instructions_success: AddInstructionsSuccess
  add_instructions_error: AddInstructionsError
  get_authentication_code: GetAuthenticationCode
  get_authentication_code_error: GetAuthenticationCodeError
  get_authentication_code_success: GetAuthenticationCodeSuccess
  submit_verification_code: SubmitVerificationCode
  submit_verification_code_success: SubmitVerificationCodeSuccess
  submit_verification_code_error: SubmitVerificationCodeError
  view_authentication: ViewAuthentication
  view_instructions: ViewInstructions
  view_verification: ViewVerification
  navigate_to_privacy_page: NavigateToPrivacyPage
  navigate_to_terms_page: NavigateToTermsPage
  navigate_to_vulnerability_report: NavigateToVulnerabilityReport
  navigate_to_security_overview: NavigateToSecurityOverview
  dismiss_instructions: DismissInstructions
  edit_address: EditAddress
  edit_address_success: EditAddressSuccess
  edit_address_error: EditAddressError
  open_privacy_choices: OpenPrivacyChoices
  pau_original_address_select: PAU_OriginalAddressSelection
  pau_original_address_select_success: PAU_OriginalAddressSelectionSuccess
  pau_original_address_select_error: PAU_OriginalAddressSelectionError
  pau_verified_address_select: PAU_VerifiedAddressSelection
  pau_verified_address_select_success: PAU_VerifiedAddressSelectionSuccess
  pau_verified_address_select_error: PAU_VerifiedAddressSelectionError
  token_login_success: TokenLoginSuccess
  token_login_error: TokenLoginError
  save_business_hours: SaveBusinessHours
  save_business_hours_cancel: SaveBusinessHours
  feedback_form_viewed: FeedbackFormViewed
  feedback_form_submitted: FeedbackFormSubmitted
  support_feedback_form_viewed: SupportFeedbackFormViewed
  support_feedback_form_submitted: SupportFeedbackFormSubmitted
  driver_info_popup_viewed: DriverInfoPopupViewed
  driver_info_interest_confirmed: DriverInfoInterestConfirmed
  driver_info_interest_declined: DriverInfoInterestDeclined
}
export const STATSIG_LOGGED_EVENTS: readonly (keyof EventMap)[] = [
  'view_instructions',
  'add_instructions_success',
  'driver_info_popup_viewed',
  'driver_info_interest_confirmed',
  'driver_info_interest_declined',
]

let _statsigClient: StatsigClient | null = null

/**
 * Registers the initialized Statsig client for use in event logging.
 * Should be called once after {@link StatsigClient.initializeAsync} resolves.
 * @param client The initialized Statsig client instance.
 */
export function setStatsigClient(client: StatsigClient): void {
  _statsigClient = client
}

/** Exposed for test teardown only — do not call in application code. */
export function _resetStatsigClient(): void {
  _statsigClient = null
}

/**
 * Returns the initialized Statsig client, or null if it has not yet been
 * registered. Used by the canonical log adapter for synchronous gate checks
 * outside of React component trees (where useGateValue is unavailable).
 */
export function getStatsigClient(): StatsigClient | null {
  return _statsigClient
}

const googleAnalyticsPlugin = googleAnalytics({
  measurementIds: [import.meta.env.REACT_APP_GOOGLE_ANALYTICS_MEASUREMENT_ID!],
  enabled: false,
})

const clarity = clarityPlugin({
  projectId: import.meta.env.REACT_APP_CLARITY_PROJECT_ID!,
  enabled: false,
})

export const analytics = Analytics({
  app: 'consumer-web-app',
  plugins: [googleAnalyticsPlugin, clarity],
})

/**
 * Read off the plugins rather than written out again. `enable`/`disable` look
 * plugins up by name and throw on a miss, so a literal that drifts from the
 * registered name breaks consent handling at runtime and nowhere else.
 */
const CONSENT_PLUGIN_NAMES = [googleAnalyticsPlugin.name, clarity.name]

/**
 * Tracks an analytics event via Google Analytics and Microsoft Clarity.
 * For events listed in {@link STATSIG_LOGGED_EVENTS}, also logs the event to Statsig.
 * @param eventName The name of the event to track.
 * @param props Event-specific properties associated with the event.
 */
export function trackEvent<K extends keyof EventMap>(eventName: K, props: EventMap[K]): void {
  analytics.track(eventName, props)

  if ((STATSIG_LOGGED_EVENTS as readonly string[]).includes(eventName)) {
    if (_statsigClient) {
      _statsigClient.logEvent(eventName, undefined, props as Record<string, string>)
    } else {
      window.newrelic?.noticeError(
        new Error(`trackEvent called for '${eventName}' before Statsig client was initialized`)
      )
    }
  }
}

/**
 * Registers DataGrail consent preference callbacks to enable or disable analytics plugins based on the user's cookie preferences.
 * Must be called after the DataGrail banner script has loaded.
 */
export function initializeDgCallbacks() {
  // We need to initialize the DG callbacks here so we can gather the preferences once datagrail loads. We can't guarantee that the DG_BANNER_API is aailable on initial render
  window.dgEvent.push({
    event: 'preference_callback',
    params: (preferences: DataGrailPreferences) => {
      if (hasPerformanceEnabled(preferences)) {
        analytics.plugins.enable(CONSENT_PLUGIN_NAMES)
      } else {
        analytics.plugins.disable(CONSENT_PLUGIN_NAMES)
      }
    },
  })
  window.dgEvent.push({
    event: 'initial_preference_callback',
    params: (preferences: DataGrailPreferences) => {
      if (hasPerformanceEnabled(preferences)) {
        analytics.plugins.enable(CONSENT_PLUGIN_NAMES)
      }
    },
  })
}

/**
 * Check the DataGrail preferences to see if the user has opted in to performance cookies.
 * @param preferences The DataGrail preferences
 * @returns True if the user has enabled performance cookies, false otherwise
 */
function hasPerformanceEnabled(preferences: DataGrailPreferences) {
  return preferences.consentPreferences.cookieOptions.some(
    option => option.gtm_key === 'dg-category-performance' && option.isEnabled
  )
}

/**
 * Represents the DataGrail preferences object.
 * @see [Reference docs](https://docs.datagrail.io/docs/consent/managing-trackers/retrieve-consent-choices)
 */
interface DataGrailPreferences {
  consentPreferences: {
    cookieOptions: {
      gtm_key: 'dg-category-essential' | 'dg-category-performance' | 'dg-category-marketing' | 'dg-category-functional'
      isEnabled: boolean
    }[]
    isCustomized: boolean
  }
  action: 'essential_only' | 'accept_all' | 'custom' | 'GPC' | 'DNT' | 'page_load' | 'exit'
  uniqueId: string
  policyName: string
}
